import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AlertModule } from '@bayclubs/alert';
import { DropDownModule } from '@bayclubs/drop-down';
import { CorrelationIdHttpInterceptor, LoggingInterceptor, LoggingModule, provideObservability } from '@bayclubs/logging';
import { SpinnerModule } from '@bayclubs/spinner';

//bayclubs modules
import { LoaderInterceptor, TranslationService, UtilsModule } from '@bayclubs/utils';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY, ReCaptchaV3Service } from 'ng-recaptcha';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { environment } from '../environments/environment';

//application modules
import { AppRoutingModule } from './app-routing.module';

//application components
import { AppComponent } from './app.component';
import { InfrastructureModule, OmsErrorHandler } from './infrastructure/infrastructure.module';
import { RemoteConfigService } from './infrastructure/services/remote-config/remote-config.service';

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        UtilsModule.forRoot(),
        LoggingModule.forRoot({
            loggingConfig: {
                consoleLogEnabled: !environment.production,
                seqLoggingEndpoint: environment.seqLoggingEndpoint,
                seqLoggingApiKey: environment.seqLoggingApiKey,
                appVersion: environment.appVersion
            }
        }),
        InfrastructureModule.forRoot(),
        ScrollToModule.forRoot(),
        AlertModule.forRoot(),
        SpinnerModule.forRoot(),
        DropDownModule.forRoot(),
        PopoverModule,
        RecaptchaV3Module
    ],
    providers: [
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: 'localization/',
                suffix: `.json?timestamp=${new Date().getTime()}`,
                useHttpBackend: true,
                failOnError: true
            }),
            fallbackLang: 'locale-en',
            lang: 'locale-en'
        }),
        // Boots the Grafana Faro browser SDK. Reads observability* + environment from
        // window.env, so it stays inert unless observabilityEnabled is the string 'true'.
        provideObservability(),
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.siteKey },
        // Eagerly construct the reCAPTCHA v3 service at bootstrap so the grecaptcha
        // script loads at app start (not lazily on the checkout route). This lets v3
        // observe user behaviour across the whole funnel, improving score quality.
        // Returns void synchronously — bootstrap does NOT wait for the script.
        provideAppInitializer(() => { inject(ReCaptchaV3Service); }),
        provideAppInitializer(() => {
          const initializerFn = (initTranslations)(inject(TranslationService));
          return initializerFn();
        }),
        // Instantiate the service so its constructor kicks off Firebase init.
        // Returns void synchronously — bootstrap does NOT wait for Firebase.
        provideAppInitializer(() => { inject(RemoteConfigService); }),
        { provide: HTTP_INTERCEPTORS, useClass: CorrelationIdHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        { provide: ErrorHandler, useClass: OmsErrorHandler },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {}

export function initTranslations(translationService: TranslationService) {
    return translationService.initSynchronously('locale-en');
}
